import type { ComponentProps, FormEvent } from 'react';
import { useRef, useState } from 'react';
import { Popover, type PopoverState } from 'react-tiny-popover';

import { Tooltip } from '#components/base/Tooltip/Tooltip';
import { IconSystem } from '#icons/IconSystem';
import { logger } from '#utils/logging/logger';

import styles from './NewsletterSection.module.scss';
import { Section } from './Section';

type Response = {
	message: string;
	success: boolean;
};

async function subscribeToNewsletter(formData: FormData): Promise<Response> {
	try {
		const response = await fetch('newsletter/subscribe', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(Object.fromEntries(formData)),
		});

		if (response.ok) {
			const data = await response.json();

			return {
				success: data.success || false,
				message: data.message || '',
			};
		} else {
			try {
				if ((response.headers.get('content-type')?.indexOf('application/json') ?? -1) > -1) {
					const data = await response.json();

					logger.error({ data }, `Newsletter Error`);

					return {
						message: data.message,
						success: false,
					};
				}
			} catch (e: any) {
				logger.error(e, `Newsletter Error`);
			}
		}
	} catch (error: any) {
		logger.error(error, `Newsletter Error`);
	}
	return {
		message: 'Unbekannter Fehler bei der Newsletter Anmeldung',
		success: false,
	};
}

type TooltipState = ComponentProps<typeof Tooltip>;

type Props = {
	className?: string;
};

export function NewsletterSection({ className }: Props) {
	const [isPending, setIsPending] = useState(false);
	const [isFormVisible, setIsFormVisible] = useState(true);
	const [tooltipState, setTooltipState] = useState<TooltipState & { visible: boolean }>({
		content: '',
		type: 'info',
		visible: false,
	});
	const emailInputRef = useRef<HTMLInputElement>(null);

	async function onSubmit(e: FormEvent<HTMLFormElement>) {
		e.preventDefault();
		if (isPending) {
			return;
		}

		// check if input is valid
		const emailValid = !!emailInputRef.current?.validity.valid;
		if (!emailValid) {
			setTooltipState({
				content: 'Ihre Eingabe muss mit dem geforderten Format übereinstimmen z.B.: max.mustermann@br.de',
				type: 'info',
				visible: true,
			});
			return;
		} else {
			setTooltipState({
				content: '',
				type: 'info',
				visible: false,
			});
		}

		const data = new FormData(e.target as any);
		setIsPending(true);
		const result = await subscribeToNewsletter(data);
		if (result.success) {
			setTooltipState({
				content: 'Anmeldebestätigung verschickt',
				type: 'success',
				visible: true,
			});
			setIsFormVisible(false);
		} else {
			setTooltipState({
				content: result.message,
				type: 'error',
				visible: true,
			});
		}
		setIsPending(false);
	}

	const { visible: tooltipVisible, ...tooltipProps } = tooltipState;

	return (
		<Section title="BR24 Newsletter" expandable={false} className={className}>
			<p>Zum Feierabend das Wichtigste aus der BR24 Redaktion.</p>
			<Popover
				isOpen={tooltipVisible}
				positions={['bottom', 'top']}
				align="start"
				containerClassName={styles.popoverContainer}
				content={({ childRect }: PopoverState) => {
					return (
						<Tooltip
							style={{
								width: childRect.width,
							}}
							{...tooltipProps}
						/>
					);
				}}
			>
				<div className={styles.formWrapper}>
					{isFormVisible && (
						<form className={`${styles.form} ${isPending ? styles.pending : ''} heading4`} onSubmit={(e) => onSubmit(e)} noValidate={true}>
							<input
								className={styles.input}
								type="email"
								name="email"
								placeholder="E-Mail eingeben"
								aria-label="E-Mail eingeben"
								ref={emailInputRef}
							></input>
							<button type="submit" className={styles.button} disabled={isPending}>
								<IconSystem icon="chevron-right" variant="filled" title="Beim Newsletter anmelden" />
							</button>
						</form>
					)}
				</div>
			</Popover>
		</Section>
	);
}
